import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Switch, Dialog, DialogPanel } from "@headlessui/react";
import { FiPlus, FiCheck } from "react-icons/fi";
import Pagination from '../../Pagination';
import { ApiDelete, ApiGet, ApiPost } from '../../../helper/axiosFunctions';
import { useParams } from 'react-router-dom';
import { commonToast } from '../../../helper/Toast';
import { useTranslation } from 'react-i18next';
import { IoSearchOutline } from 'react-icons/io5';
import { BsFolder2Open } from 'react-icons/bs';
import { toast } from 'sonner';
import { IoMdClose } from 'react-icons/io';
import { useSelector } from 'react-redux';
import AddEditQuestionModel from '../../model/AddEditQuestionModel';
import ImportCardFromDeckModal from '../../model/ImportCardFromDeckModal';
import { removeAlertPopup } from '../../../helper/commonFunctions';
import SubjectOverview from '../../model/SubjectOverview';
import Loading from '../../Loading';
import Button from '../../ui/Button';
import DeckCardsPanel from '../../DeckCardsPanel';

export default function WorkshopDetailQuestions({
    deckDetails,
    onQuestionCountChange,
    onDeckDetailsRefresh,
}) {
    const { t } = useTranslation();
    const importFileRef = useRef(null)
    const mediaUploadRef = useRef(null)
    const { id: deckId } = useParams();
    const [deckCards, setDeckCards] = useState([])
    const [editingCard, setEditingCard] = useState({})
    const [addEditQuestionModel, setAddEditQuestionModel] = useState(false);
    const [cardOverviewModel, setCardOverviewModel] = useState(false)
    const [loading, setLoading] = useState(true);

    // Import Card (from another deck) modal
    const [isImportCardModalOpen, setIsImportCardModalOpen] = useState(false);

    // Import Questions modal: text editor (simplified format) + media uploads
    const [showImportQuestionsModal, setShowImportQuestionsModal] = useState(false);
    const [importTextContent, setImportTextContent] = useState("");
    const [selectedMediaFiles, setSelectedMediaFiles] = useState([]);
    const [uploadingMedia, setUploadingMedia] = useState(false);

    // 2-step import (validate -> create)
    const [validationResponse, setValidationResponse] = useState(null);
    const [validatedFileContent, setValidatedFileContent] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState(null);
    const [showImportPreview, setShowImportPreview] = useState(false);
    const [isValidatingImport, setIsValidatingImport] = useState(false);
    const [isCreatingFromImport, setIsCreatingFromImport] = useState(false);
    const [isAskingAI, setIsAskingAI] = useState(false);
    const [aiResponse, setAiResponse] = useState(null);

    // Inline select mode
    const [selectMode, setSelectMode] = useState(false);
    const [selectedCardIds, setSelectedCardIds] = useState([]);
    const [showDeckPickerModal, setShowDeckPickerModal] = useState(false);
    const [pickerDecks, setPickerDecks] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerSelectedDecks, setPickerSelectedDecks] = useState([]);
    const [pickerSearchQuery, setPickerSearchQuery] = useState('');
    const [pickerAssigning, setPickerAssigning] = useState(false);
    const user_data = useSelector((state) => state.profileData.profileData);

    const resetImportStates = () => {
        setValidationResponse(null);
        setValidatedFileContent(null);
        setSelectedFileName(null);
        setShowImportPreview(false);
        setIsValidatingImport(false);
        setIsCreatingFromImport(false);
        setIsAskingAI(false);
        setAiResponse(null);
    };

    const handleCancelImport = () => {
        resetImportStates();
        toast.info(t("deckCreationCancelled") || "Cancelled");
    };


    // Create a mapping of cardId to complexity from deck details
    const complexityMap = useMemo(() => {
        if (!deckDetails?.cards) return {};
        const map = {};
        deckDetails.cards.forEach(card => {
            map[card.cardId] = card.complexity;
        });
        return map;
    }, [deckDetails]);

    // Open the Import Questions modal (text editor + media uploads)
    const openImportQuestionsModal = () => {
        if (!deckId) {
            commonToast('Deck ID not found. Please refresh the page.', 'error');
            return;
        }
        setImportTextContent("");
        setSelectedFileName(null);
        setSelectedMediaFiles([]);
        setShowImportQuestionsModal(true);
    };

    const closeImportQuestionsModal = () => {
        setShowImportQuestionsModal(false);
        setImportTextContent("");
        setSelectedFileName(null);
        setSelectedMediaFiles([]);
    };

    // .txt file picker (inside the modal) — load file contents into the editor
    // so the user can review/edit before importing.
    const handleImportFileChosen = async (e) => {
        const file = e.target.files?.[0] || null;
        try {
            if (!file) return;

            if (!file.name.toLowerCase().endsWith(".txt")) {
                toast.error(t("fileShouldBeTextDocument") || "Please select a .txt file");
                return;
            }

            const text = await file.text();
            setImportTextContent(text);
            setSelectedFileName(file.name);
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Error reading file");
        } finally {
            e.target.value = null;
        }
    };

    // Image / audio file picker — accumulate the user's selection so they can
    // be uploaded as deck assets when the user clicks Import.
    const handleMediaFileSelection = (e) => {
        const files = Array.from(e.target.files || []);
        const validFiles = files.filter(file => {
            const isImage = file.type.startsWith('image/');
            const isAudio = file.type.startsWith('audio/');
            if (!isImage && !isAudio) {
                commonToast(`${file.name} is not a valid image or audio file`, 'error');
                return false;
            }
            return true;
        });
        setSelectedMediaFiles(prev => [...prev, ...validFiles]);
        e.target.value = null;
    };

    const removeMediaFile = (index) => {
        setSelectedMediaFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Upload selected images/audio to the deck. Returns true on success (or
    // when there is nothing to upload), false on failure.
    const uploadSelectedMedia = async () => {
        if (selectedMediaFiles.length === 0) return true;
        const folderOrDeckId = deckDetails?.folder || deckId;
        if (!folderOrDeckId) {
            commonToast('Deck ID or folder is required for file upload', 'error');
            return false;
        }
        try {
            setUploadingMedia(true);
            const formData = new FormData();
            formData.append("deckId", folderOrDeckId);
            selectedMediaFiles.forEach((file, index) => {
                formData.append(`file${index + 1}`, file);
            });
            const response = await ApiPost('/contents/images-upload/deck', formData);
            if (response?.data) {
                commonToast(`${selectedMediaFiles.length} file(s) uploaded successfully`, 'success');
                setSelectedMediaFiles([]);
                return true;
            }
            return false;
        } catch (error) {
            commonToast(error?.data?.errorDescription || error?.data?.error || error?.message || 'Failed to upload files', 'error');
            return false;
        } finally {
            setUploadingMedia(false);
        }
    };

    // Validate the simplified-text-format content (from textarea or .txt file)
    // and open the existing preview modal on success.
    const validateImportText = async () => {
        const text = (importTextContent || "").trim();
        if (!text) return true;
        if (!deckId) {
            toast.error(t("deckIdMissing") || "Deck ID not found");
            return false;
        }
        try {
            setIsValidatingImport(true);
            setValidatedFileContent(text);
            const res = await ApiPost("/contents/deck-text-format/cards/validate", {
                deckId,
                text,
            });
            setValidationResponse(res?.data ?? res);
            setShowImportPreview(true);
            return true;
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Error validating content");
            return false;
        } finally {
            setIsValidatingImport(false);
        }
    };

    // Single Import action: uploads media (if any) and validates the text
    // content (if any). Refreshes the cards list and closes the modal as
    // appropriate.
    const handleImportQuestionsSubmit = async () => {
        const hasText = (importTextContent || "").trim().length > 0;
        const hasMedia = selectedMediaFiles.length > 0;
        if (!hasText && !hasMedia) {
            toast.error(t("pasteOrUploadContent") || "Please paste content or upload a file first");
            return;
        }

        const mediaOk = await uploadSelectedMedia();
        if (!mediaOk) return;

        if (hasText) {
            const textOk = await validateImportText();
            if (!textOk) return;
            setShowImportQuestionsModal(false);
        } else {
            // Media-only path — refresh deck cards and close.
            setShowImportQuestionsModal(false);
            fetchDeckCards();
        }
    };

    const handleAskAI = async () => {
        if (!validatedFileContent) return;

        try {
            setIsAskingAI(true);
            setAiResponse(null);

            // Use ApiPost with common authentication
            const response = await ApiPost("/ai-api/suggestions", {
                text: validatedFileContent,
            });

            setAiResponse(response?.data || response);
            console.log("AI Response:", response);
        } catch (error) {
            console.error("AI API Error:", error);
            commonToast(error?.data?.errorDescription || error?.data?.error || error?.message || "Failed to get AI suggestions", "error");
        } finally {
            setIsAskingAI(false);
        }
    };

    const handleConfirmImport = async () => {
        if (!validatedFileContent || !deckId) return;

        if (validationResponse?.errorResponse && validationResponse.errorResponse.trim() !== "") {
            toast.error(
                t("fixValidationErrors") || "Please fix validation errors before importing"
            );
            return;
        }

        try {
            setIsCreatingFromImport(true);

            await ApiPost("/contents/deck-text-format/cards", {
                deckId,
                text: validatedFileContent,
            });

            toast.success(t("cardsImportedSuccess") || "Cards imported successfully");

            await fetchDeckCards();
            resetImportStates();
        } catch (error) {
            const msg = error?.response?.data?.message || error?.message || t("errorAddingDeck") || "Failed to import cards";
            toast.error(msg);
        } finally {
            setIsCreatingFromImport(false);
        }
    };

    const toggleSelectMode = () => {
        setSelectMode(prev => !prev);
        setSelectedCardIds([]);
    };

    const toggleCardSelection = (cardId) => {
        setSelectedCardIds(prev =>
            prev.includes(cardId) ? prev.filter(id => id !== cardId) : [...prev, cardId]
        );
    };

    const handleSelectAll = () => {
        if (selectedCardIds.length === deckCards.length) {
            setSelectedCardIds([]);
        } else {
            setSelectedCardIds(deckCards.map(c => c.cardId));
        }
    };

    const openDeckPicker = async () => {
        setShowDeckPickerModal(true);
        setPickerSelectedDecks([]);
        setPickerSearchQuery('');
        setPickerLoading(true);
        try {
            const res = await ApiGet(`/contents/decks/authors/${user_data?.userId}`);
            const otherDecks = (res.data || []).filter(d => d.deckId !== deckId);
            setPickerDecks(otherDecks);
        } catch (err) {
            commonToast(err?.data?.errorDescription || err?.message || 'Failed to load decks', 'error');
        } finally {
            setPickerLoading(false);
        }
    };

    const closeDeckPicker = () => {
        setShowDeckPickerModal(false);
        setPickerSelectedDecks([]);
        setPickerSearchQuery('');
    };

    const togglePickerDeck = (id) => {
        setPickerSelectedDecks(prev =>
            prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
        );
    };

    const filteredPickerDecks = useMemo(() => {
        if (!pickerSearchQuery.trim()) return pickerDecks;
        const q = pickerSearchQuery.toLowerCase();
        return pickerDecks.filter(d =>
            (d.name || d.deckName || '').toLowerCase().includes(q)
        );
    }, [pickerDecks, pickerSearchQuery]);

    const handlePickerAssign = async () => {
        if (pickerSelectedDecks.length === 0 || selectedCardIds.length === 0) return;
        setPickerAssigning(true);
        let successCount = 0;
        let errorCount = 0;

        for (const cardId of selectedCardIds) {
            const cardComplexity = complexityMap[cardId] || 3;
            for (const targetDeckId of pickerSelectedDecks) {
                try {
                    await ApiPost('/contents/decks/cards', {
                        deckId: targetDeckId,
                        card: { cardId, complexity: cardComplexity, sortOrder: 0 },
                    });
                    successCount++;
                } catch (error) {
                    errorCount++;
                    const card = deckCards.find(c => c.cardId === cardId);
                    const errorMsg = error?.data?.errorDescription || error?.data?.error || error?.message || 'Assignment failed';
                    commonToast(`${card?.cardName || cardId}: ${errorMsg}`, 'error');
                }
            }
        }

        if (successCount > 0) {
            commonToast(`${successCount} card(s) assigned successfully`, 'success');
        }
        setPickerAssigning(false);
        closeDeckPicker();
        setSelectMode(false);
        setSelectedCardIds([]);
    };

    const handleEditCard = (deck) => {
        setEditingCard(deck);
        setAddEditQuestionModel(true);
    };

    const handleOverviewCard = (deck) => {
        setEditingCard(deck);
        setCardOverviewModel(true);
    };

    const handleDeleteCard = (card) => {
        removeAlertPopup({
            title: t("areYouSureYouWantToDelete"),
            t: t,
            preConfirm: async () => {
                await ApiDelete(`/contents/decks/${deckId}/cards/${card?.cardId}`)
                    .then(() => {
                        commonToast(t('deckDeletedSuccess'), 'success');
                        fetchDeckCards();
                    })
                    .catch((error) => {
                        commonToast(error?.data?.errorDescription || error?.data?.error || error?.message, 'error');
                    })
            }
        })
    };

    const fetchDeckCards = async () => {
        try {
            setLoading(true);
            const response = await ApiGet(`/contents/decks/${deckId}/cards`);
            const cards = response.data || [];
            setDeckCards(cards);
            onQuestionCountChange?.(cards.length);
        } catch (error) {
            commonToast(error?.data?.errorDescription || error?.data?.error || error?.message, "error");
        } finally {
            setLoading(false);
        }
    };

    // After create/edit, also refresh deck details — complexity lives on the
    // deck↔card link inside deckDetails.cards, not on the cards list payload.
    const refreshCardsAndDeckDetails = async () => {
        await fetchDeckCards();
        await onDeckDetailsRefresh?.();
    };

    useEffect(() => {

        if (deckId) {
            fetchDeckCards();
        }
    }, [deckId, deckDetails]);
    const questionActions = (
                <>
                    {!selectMode ? (
                        <>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setEditingCard({});
                                    setAddEditQuestionModel(true);
                                }}
                            >
                                <FiPlus size={16} />
                                {t("manualAdd")}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => setIsImportCardModalOpen(true)}
                            >
                                <BsFolder2Open size={14} className="text-gray-500" />
                                {t("importCard") || "Import Card"}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={openImportQuestionsModal}
                            >
                                <FiPlus size={16} className="text-gray-500" />
                                {t("importQuestions") || "Import Questions"}
                            </Button>
                            {deckCards.length > 0 && (
                                <Button variant="soft" onClick={toggleSelectMode}>
                                    <FiCheck size={15} />
                                    {t("select") || "Select"}
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <span className="inline-flex h-9 items-center rounded-2xl bg-blue-50 px-3 text-sm font-semibold text-blue-700">
                                {selectedCardIds.length}/{deckCards.length}
                            </span>
                            <Button variant="soft" onClick={handleSelectAll}>
                                {selectedCardIds.length === deckCards.length
                                    ? t("deselectAll") || "Deselect All"
                                    : t("selectAll") || "Select All"}
                            </Button>
                            <Button variant="secondary" onClick={toggleSelectMode}>
                                {t("cancel") || "Cancel"}
                            </Button>
                        </>
                    )}
                    <input
                        type="file"
                        ref={importFileRef}
                        accept=".txt"
                        onChange={handleImportFileChosen}
                        hidden
                    />
                </>
    );

    return (
        <>
            <DeckCardsPanel
                cards={deckCards}
                links={deckDetails?.cards}
                deckId={deckId}
                enableReorder
                loading={loading && deckCards.length === 0}
                onOrderSaved={onDeckDetailsRefresh}
                storageKey="workshop_deck_cards_view"
                toolbar={questionActions}
                selectMode={selectMode}
                selectedCardIds={selectedCardIds}
                onSelect={toggleCardSelection}
                onEdit={handleEditCard}
                onDelete={handleDeleteCard}
                onDetail={handleOverviewCard}
                emptyLabel="No questions found."
            />
            {/* {deckCards?.length > 0 && <Pagination />} */}

            {/* Floating bottom bar in select mode */}
            {selectMode && selectedCardIds.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t shadow-[0_-4px_20px_rgba(0,0,0,0.1)] p-4">
                    <div className="max-w-4xl mx-auto flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">
                            {selectedCardIds.length} {selectedCardIds.length === 1 ? (t("cardSelected") || "card selected") : (t("cardsSelected") || "cards selected")}
                        </p>
                        <Button variant="primary" size="lg" onClick={openDeckPicker}>
                            <BsFolder2Open className="text-lg" />
                            {t("assignToDeck") || "Assign to Deck"}
                        </Button>
                    </div>
                </div>
            )}

            {/* Deck Picker Modal */}
            <Dialog open={showDeckPickerModal} onClose={closeDeckPicker} className="relative z-50">
                <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <DialogPanel className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-xl">
                        <div className="flex items-center justify-between p-5 border-b">
                            <h2 className="text-lg font-semibold">{t("selectDeck") || "Select Deck"}</h2>
                            <button onClick={closeDeckPicker} className="p-1 hover:bg-gray-100 rounded-lg">
                                <IoMdClose className="text-xl text-gray-500" />
                            </button>
                        </div>

                        <div className="px-5 pt-4 pb-2">
                            <div className="relative">
                                <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={t("searchDecks") || "Search decks..."}
                                    value={pickerSearchQuery}
                                    onChange={(e) => setPickerSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-5 py-2 space-y-2">
                            {pickerLoading ? (
                                <div className="flex justify-center py-10"><Loading /></div>
                            ) : filteredPickerDecks.length === 0 ? (
                                <p className="text-center text-gray-400 py-10 text-sm">{t("noDecksFound") || "No decks found"}</p>
                            ) : (
                                filteredPickerDecks.map((deck) => {
                                    const isChecked = pickerSelectedDecks.includes(deck.deckId);
                                    return (
                                        <div
                                            key={deck.deckId}
                                            onClick={() => togglePickerDeck(deck.deckId)}
                                            className={`flex items-center gap-3 p-3 rounded-2xl cursor-pointer border transition-all ${
                                                isChecked ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                                                isChecked ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                                            }`}>
                                                {isChecked && <FiCheck className="text-white text-xs" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{deck.name || deck.deckName || 'Unnamed Deck'}</p>
                                                {deck.description && (
                                                    <p className="text-xs text-gray-400 truncate">{deck.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="p-5 border-t flex items-center justify-between gap-3">
                            <p className="text-xs text-gray-500">
                                {pickerSelectedDecks.length} {t("decksSelected") || "deck(s) selected"}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={closeDeckPicker}
                                    className="px-4 py-2 text-sm border rounded-2xl hover:bg-gray-50"
                                >
                                    {t("cancel") || "Cancel"}
                                </button>
                                <button
                                    onClick={handlePickerAssign}
                                    disabled={pickerSelectedDecks.length === 0 || pickerAssigning}
                                    className={`px-5 py-2 text-sm text-white rounded-2xl font-medium ${
                                        pickerSelectedDecks.length === 0 || pickerAssigning
                                            ? 'bg-gray-300 cursor-not-allowed'
                                            : 'bg-darkblue hover:opacity-90'
                                    }`}
                                >
                                    {pickerAssigning ? (t("assigning") || "Assigning...") : (t("assign") || "Assign")}
                                </button>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </Dialog>

            <AddEditQuestionModel
                open={addEditQuestionModel}
                setIsOpen={setAddEditQuestionModel}
                editMode={Boolean(Object.keys(editingCard).length)}
                editingCard={editingCard}
                fetchDeckCards={refreshCardsAndDeckDetails}
                deckDetails={deckDetails}
            />
            <SubjectOverview
                isDetail={cardOverviewModel}
                setIsDetail={setCardOverviewModel}
                data={editingCard}
                currentDeckId={deckDetails?.deckId}
                complexity={complexityMap[editingCard?.cardId]}
            />

            {/* Import Card from another deck */}
            <ImportCardFromDeckModal
                isOpen={isImportCardModalOpen}
                setIsOpen={setIsImportCardModalOpen}
                currentDeckId={deckId}
                onSuccess={fetchDeckCards}
            />

            {/* Import Questions Modal: text editor (simplified format) + media uploads */}
            {showImportQuestionsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl overflow-hidden">
                        <div className="flex justify-between items-center p-5 border-b">
                            <h2 className="text-xl font-semibold">
                                {t("importQuestions") || "Import Questions"}
                            </h2>
                            <button
                                onClick={closeImportQuestionsModal}
                                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                            >
                                ×
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-5">
                            {/* .txt file chooser (loads into editor) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("uploadFromFile") || "Upload from a file"}
                                </label>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => importFileRef.current?.click()}
                                        className="px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-all text-sm font-medium"
                                    >
                                        {t("chooseFile") || "Choose Files"}
                                    </button>
                                    <span className="text-sm text-gray-500 truncate">
                                        {selectedFileName || (t("noFileChosen") || "No file chosen")}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                    {t("acceptedTxtOnly") || "Accepts .txt files in the simplified format"}
                                </p>
                            </div>

                            {/* Divider with OR */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-gray-200" />
                                <span className="text-xs uppercase text-gray-400 font-medium">
                                    {t("or") || "or"}
                                </span>
                                <div className="flex-1 h-px bg-gray-200" />
                            </div>

                            {/* Simplified-format text editor */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("pasteSimplifiedText") || "Paste simplified text format"}
                                </label>
                                <textarea
                                    value={importTextContent}
                                    onChange={(e) => {
                                        setImportTextContent(e.target.value);
                                        if (selectedFileName) setSelectedFileName(null);
                                    }}
                                    placeholder={t("pasteSimplifiedTextPlaceholder") || "Paste your questions in the simplified text format here..."}
                                    rows={10}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm font-mono resize-y"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    {importTextContent.length} {t("characters") || "characters"}
                                </p>
                            </div>

                            {/* Section divider */}
                            <div className="border-t border-gray-200" />

                            {/* Image / audio uploads (deck assets) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("uploadImagesAndAudio") || "Upload Images & Audio"}
                                </label>
                                <div
                                    onClick={() => mediaUploadRef.current?.click()}
                                    className="w-full p-4 border-2 border-dashed border-blue-300 rounded-lg flex flex-col items-center justify-center cursor-pointer bg-blue-50 hover:bg-blue-100 transition-all duration-200"
                                >
                                    <svg className="h-7 w-7 text-blue-400 mb-1" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <span className="text-blue-500 text-sm">
                                        {t("clickToUploadFiles") || "Click to Upload Files"}
                                    </span>
                                    <span className="text-gray-400 text-xs mt-1">
                                        {t("supportsImagesAndAudio") || "Supports Images & Audio Files"}
                                    </span>
                                </div>

                                {selectedMediaFiles.length > 0 && (
                                    <div className="mt-3">
                                        <h3 className="text-xs font-medium text-gray-600 mb-2">
                                            {t("selectedFiles") || "Selected Files"} ({selectedMediaFiles.length})
                                        </h3>
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {selectedMediaFiles.map((file, index) => (
                                                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        {file.type.startsWith('image/') ? (
                                                            <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                                            </svg>
                                                        )}
                                                        <span className="text-xs text-gray-700 truncate">{file.name}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => removeMediaFile(index)}
                                                        className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50">
                            <Button variant="secondary" onClick={closeImportQuestionsModal}>
                                {t("cancel") || "Cancel"}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleImportQuestionsSubmit}
                                disabled={
                                    isValidatingImport
                                    || uploadingMedia
                                    || (!importTextContent.trim() && selectedMediaFiles.length === 0)
                                }
                            >
                                {uploadingMedia
                                    ? (t("uploading") || "Uploading...")
                                    : isValidatingImport
                                        ? (t("validating") || "Validating...")
                                        : (t("import") || "Import")}
                            </Button>
                        </div>

                        <input
                            type="file"
                            ref={mediaUploadRef}
                            accept="image/*,audio/*"
                            multiple
                            onChange={handleMediaFileSelection}
                            hidden
                        />
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {isValidatingImport && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <div className="text-center">
                            <h3 className="text-lg font-semibold text-gray-800">{t("validatingFile") || "Validating file..."}</h3>
                            <p className="text-sm text-gray-600 mt-2">{t("pleaseWaitProcessing") || "Please wait while we process your file..."}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Preview Modal */}
            {showImportPreview && validationResponse && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-xl max-h-[95vh] overflow-hidden shadow-xl">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b">
                            <h2 className="text-xl font-semibold text-gray-800">
                                {t("sampleDeckPreview") || "Sample Deck Preview"}
                            </h2>
                            <button
                                onClick={handleCancelImport}
                                className="text-gray-500 hover:text-gray-700 text-2xl"
                            >
                                ×
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 max-h-[70vh] overflow-y-auto ">
                            {/* Deck Errors - Red Box (shown first) */}
                            {validationResponse?.errorResponse && validationResponse.errorResponse.trim() !== "" && (
                                <div className="mb-4">
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <div className="text-sm text-red-800">
                                            <p><strong>Deck Errors:</strong></p>
                                            <div className="mt-2 whitespace-pre-line">
                                                {validationResponse.errorResponse}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Deck Warnings - Yellow Box */}
                            {validationResponse?.warningResponse && validationResponse.warningResponse.trim() !== "" && (
                                <div className="mb-4">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                        <div className="text-sm text-yellow-800">
                                            <p><strong>Deck Warnings:</strong></p>
                                            <div className="mt-2 whitespace-pre-line">
                                                {validationResponse.warningResponse}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* AI Response Section */}
                            {aiResponse && (
                                <div className="mb-4">
                                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                                        <div className="text-sm text-sky-800">
                                            <p><strong>AI Suggestions:</strong></p>
                                            <div className="mt-2">
                                                {typeof aiResponse === 'string' ? (
                                                    <div className="whitespace-pre-line">{aiResponse}</div>
                                                ) : aiResponse.questions ? (
                                                    <div className="space-y-3">
                                                        {aiResponse.questions.map((question, index) => (
                                                            <div key={index} className=" p-3 rounded-lg border border-sky-100">
                                                                <h4 className="font-semibold text-sky-900 mb-2">
                                                                    {question.name || `Question ${index + 1}`}
                                                                </h4>
                                                                <div className="space-y-2">
                                                                    {question.grammar && (
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-gray-900">Grammar: {question.grammar}</span>
                                                                        </div>
                                                                    )}
                                                                    {question.information && (
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-gray-900">Information: {question.information}</span>
                                                                        </div>
                                                                    )}
                                                                    {question.copyright && (
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-gray-900">Copyright: {question.copyright}</span>
                                                                        </div>
                                                                    )}
                                                                    {question.help1 && (
                                                                        <div className="mt-2">
                                                                            <span className="font-medium text-gray-900">Help 1:</span>
                                                                            <p className="text-gray-900 mt-1">{question.help1}</p>
                                                                        </div>
                                                                    )}
                                                                    {question.help2 && (
                                                                        <div className="mt-2">
                                                                            <span className="font-medium text-gray-900">Help 2:</span>
                                                                            <p className="text-gray-900 mt-1">{question.help2}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-gray-600">
                                                        AI response received successfully. Please check the suggestions above.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Deck Information - Green Box */}
                            {validationResponse?.informationalResponse && (
                                <div className="mb-4">
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <div className="text-sm text-green-800">
                                            <p><strong>Deck Information:</strong></p>
                                            <div className="mt-2 whitespace-pre-line">
                                                {validationResponse.informationalResponse}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>



                        {/* Footer with Buttons */}
                        <div className="bg-secondarygray p-6 border-t border-gray-200">
                            <div className="flex gap-3">
                                <Button
                                    variant="soft"
                                    size="lg"
                                    className="flex-1"
                                    onClick={handleAskAI}
                                    disabled={isAskingAI || !validatedFileContent}
                                >
                                    {isAskingAI ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-darkblue"></div>
                                            {t("askingAI") || "Asking AI..."}
                                        </div>
                                    ) : (
                                        t("askAI") || "Ask AI"
                                    )}
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    className="flex-1"
                                    onClick={handleCancelImport}
                                >
                                    {t("verifyAgain") || "Verify Again"}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="lg"
                                    className="flex-1"
                                    onClick={handleConfirmImport}
                                    disabled={!!validationResponse?.errors?.length || isCreatingFromImport}
                                >
                                    {isCreatingFromImport ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            {t("processingAndAdding") || "Processing..."}
                                        </div>
                                    ) : (
                                        t("addDeck") || "Import"
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
